import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type {
  InitiateAnalysisInput,
  AnalysisStatusResponse,
  AnalysisResult,
  AnalysisStatus,
} from '../types/acquisitionAnalysis';

export interface UseAcquisitionAnalysis {
  initiateAnalysis: (input: InitiateAnalysisInput) => Promise<string>;
  pollStatus: (analysisId: string, companyId: string) => Promise<void>;
  reset: () => void;
  status: AnalysisStatus | null;
  result: AnalysisResult | null;
  isLoading: boolean;
  error: Error | null;
  progress: number;
  currentStep: string | null;
  estimatedTimeRemaining: number | null;
}

// Demo mode: simulate processing since backend processor not implemented yet
// Set to true for demos when backend processor is not available
const DEMO_MODE = false;

const PROGRESS_STEPS = [
  { progress: 20, step: 'Extracting commission data...', delay: 2000 },
  { progress: 40, step: 'Analyzing growth patterns...', delay: 2000 },
  { progress: 60, step: 'Calculating valuations...', delay: 2000 },
  { progress: 80, step: 'Assessing risk factors...', delay: 2000 },
  { progress: 95, step: 'Generating report...', delay: 1500 },
  { progress: 100, step: 'Analysis complete', delay: 500 },
];

export function useAcquisitionAnalysis(): UseAcquisitionAnalysis {
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  const [demoStepIndex, setDemoStepIndex] = useState(0);

  const initiateAnalysis = useCallback(async (input: InitiateAnalysisInput): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      setStatus('pending');
      setProgress(0);
      setCurrentStep('Initiating analysis...');
      setDemoStepIndex(0); // Reset demo progress
      setResult(null); // Clear previous result

      const initiateFunc = httpsCallable<InitiateAnalysisInput, { analysisId: string }>(
        functions,
        'initiateAcquisitionAnalysis'
      );
      const response = await initiateFunc(input);
      const analysisId = response.data.analysisId;

      setStatus('processing');
      setProgress(10);
      setCurrentStep('Uploading files...');

      return analysisId;
    } catch (err: any) {
      console.error('[Acquisition Analysis] initiateAnalysis error:', err);
      setError(err);
      setStatus('failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setStatus(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setCurrentStep(null);
    setEstimatedTimeRemaining(null);
    setDemoStepIndex(0);
    setIsLoading(false);
  }, []);

  const pollStatus = useCallback(async (analysisId: string, companyId: string): Promise<void> => {
    // Demo mode: simulate progress steps
    if (DEMO_MODE) {
      if (demoStepIndex < PROGRESS_STEPS.length) {
        const step = PROGRESS_STEPS[demoStepIndex];
        setProgress(step.progress);
        setCurrentStep(step.step);
        setDemoStepIndex((prev) => prev + 1);

        if (step.progress >= 100) {
          setStatus('completed');
          setResult(getMockAnalysisResult());
        }
      }
      return;
    }

    // Real mode: call backend
    try {
      setIsLoading(true);
      setError(null);

      console.log('[Acquisition Analysis] Polling status for', { analysisId, companyId });

      const statusFunc = httpsCallable<{ analysisId: string; companyId: string }, AnalysisStatusResponse>(
        functions,
        'getAnalysisStatus'
      );
      const response = await statusFunc({ analysisId, companyId });
      const statusData = response.data;

      console.log('[Acquisition Analysis] Status response:', {
        status: statusData.status,
        progress: statusData.progress,
        currentStep: statusData.currentStep,
        hasResult: !!statusData.result,
        hasError: !!statusData.error,
      });

      setStatus(statusData.status);
      setProgress(statusData.progress ?? 0);
      setCurrentStep(statusData.currentStep || null);
      setEstimatedTimeRemaining(statusData.estimatedTimeRemaining || null);

      if (statusData.result) {
        console.log('[Acquisition Analysis] Setting result:', statusData.result.targetName);
        setResult(statusData.result);
      }

      if (statusData.error) {
        console.error('[Acquisition Analysis] Backend error:', statusData.error);
        setError(new Error(statusData.error));
      }
    } catch (err: any) {
      console.error('[Acquisition Analysis] pollStatus error:', err);
      setError(err);
      setStatus('failed');
    } finally {
      setIsLoading(false);
    }
  }, [demoStepIndex]);

  return {
    initiateAnalysis,
    pollStatus,
    reset,
    status,
    result,
    isLoading,
    error,
    progress,
    currentStep,
    estimatedTimeRemaining,
  };
}

// Mock data generator for development
function getMockAnalysisResult(): AnalysisResult {
  return {
    analysisId: `analysis-${Date.now()}`,
    targetName: 'Entourage',
    analysisDate: new Date().toISOString(),
    dataPeriod: 'July 2023 - November 2025',
    verdict: 'CAUTION',
    executiveSummary: {
      annualTrailIncome: 1104450,
      yoyGrowth: 17.2,
      ytdGrowth: 22.3,
      brokerCount: 19,
      brokerAttrition: 0,
      clawbackRate: 1.0,
    },
    trailIncomeYoY: [
      {
        fiscalYear: 'FY23-24',
        period: 'Jul 2023 - Jun 2024',
        trailIncome: 942661,
        yoyChange: null,
      },
      {
        fiscalYear: 'FY24-25',
        period: 'Jul 2024 - Jun 2025',
        trailIncome: 1104450,
        yoyChange: 17.2,
      },
      {
        fiscalYear: 'FY25-26',
        period: 'Jul 2025 - Nov 2025',
        trailIncome: 529473,
        yoyChange: 22.3,
      },
    ],
    fyForecast: {
      months: [
        { month: 'July', priorYearActual: 83030, currentYear: 102263, status: 'actual', growth: 23.2 },
        { month: 'August', priorYearActual: 86757, currentYear: 106028, status: 'actual', growth: 22.2 },
        { month: 'September', priorYearActual: 87633, currentYear: 106333, status: 'actual', growth: 21.3 },
        { month: 'October', priorYearActual: 85654, currentYear: 103693, status: 'actual', growth: 21.1 },
        { month: 'November', priorYearActual: 89852, currentYear: 111155, status: 'actual', growth: 23.7 },
        { month: 'December', priorYearActual: 88142, currentYear: 96956, status: 'forecast', growth: null },
        { month: 'January', priorYearActual: 100480, currentYear: 110528, status: 'forecast', growth: null },
        { month: 'February', priorYearActual: 84720, currentYear: 93192, status: 'forecast', growth: null },
        { month: 'March', priorYearActual: 89772, currentYear: 98749, status: 'forecast', growth: null },
        { month: 'April', priorYearActual: 104582, currentYear: 115040, status: 'forecast', growth: null },
        { month: 'May', priorYearActual: 99739, currentYear: 109713, status: 'forecast', growth: null },
        { month: 'June', priorYearActual: 104089, currentYear: 114497, status: 'forecast', growth: null },
      ],
      fullYearActual: 1104450,
      fullYearForecast: 1268148,
      forecastGrowth: 14.8,
      valuationUpdate: {
        multiplier5x: { priorYear: 5522251, forecast: 6340740 },
        multiplier6x: { priorYear: 6626701, forecast: 7608888 },
      },
    },
    seasonality: [
      { month: 'July', fy1: 73253, fy2: 83030, fy3: 102263, trend: 'Growing' },
      { month: 'August', fy1: 77284, fy2: 86757, fy3: 106028, trend: 'Growing' },
      { month: 'September', fy1: 75250, fy2: 87633, fy3: 106333, trend: 'Growing' },
      { month: 'October', fy1: 75623, fy2: 85654, fy3: 103693, trend: 'Growing' },
      { month: 'November', fy1: 77141, fy2: 89852, fy3: 111155, trend: 'Growing' },
      { month: 'December', fy1: 76393, fy2: 88142, fy3: null, trend: 'Growing' },
      { month: 'January', fy1: 80841, fy2: 100480, fy3: null, trend: 'Growing' },
      { month: 'February', fy1: 80775, fy2: 84720, fy3: null, trend: 'Stable' },
      { month: 'March', fy1: 76714, fy2: 89772, fy3: null, trend: 'Growing' },
      { month: 'April', fy1: 82955, fy2: 104582, fy3: null, trend: 'Strong' },
      { month: 'May', fy1: 81885, fy2: 99739, fy3: null, trend: 'Growing' },
      { month: 'June', fy1: 84548, fy2: 104089, fy3: null, trend: 'Strong' },
    ],
    valuationSummary: {
      baseTrailIncome: 1104450,
      multipliers: {
        multiplier2x: 2208900,
        multiplier3x: 3313351,
        multiplier4x: 4417801,
        multiplier5x: 5522251,
        multiplier6x: 6626701,
      },
      supportingFactors: [
        'Trail growing +17% YoY (accelerating to +22%)',
        'Zero broker attrition',
        'Multiple brokers with 100%+ individual growth',
        'Very low clawback rate (~1%)',
        'Additional non-mortgage income ($102k)',
      ],
    },
    brokerPerformance: {
      topBrokers: [
        { rank: 1, brokerName: 'Sarah Thomson', annualTrail: 741285, percentOfBook: 67.1 },
        { rank: 2, brokerName: 'Elizabeth McRae', annualTrail: 105022, percentOfBook: 9.5 },
        { rank: 3, brokerName: 'Sarah Daly', annualTrail: 88461, percentOfBook: 8.0 },
        { rank: 4, brokerName: 'Shilo Hughes', annualTrail: 40117, percentOfBook: 3.6 },
        { rank: 5, brokerName: 'Astrid Caddeo', annualTrail: 35431, percentOfBook: 3.2 },
      ],
      growthTrajectory: [
        { brokerName: 'Alyssa Russo', fy1: 6193, fy2: 16525, growth: 166.8 },
        { brokerName: 'Sarah Daly', fy1: 34066, fy2: 88461, growth: 159.7 },
        { brokerName: 'Shilo Hughes', fy1: 17250, fy2: 40117, growth: 132.6 },
        { brokerName: 'Bree Sturges', fy1: 10522, fy2: 22097, growth: 110.0 },
      ],
      brokerRetention: {
        newBrokers: 4,
        leftBrokers: 0,
        attrition: 0,
      },
    },
    riskAssessment: {
      clawbackExposure: {
        totalClawbacks: 33920,
        percentOfUpfront: 1.0,
        assessment: 'Excellent',
      },
      additionalRevenue: {
        nonMortgageIncome: 102074,
      },
    },
    summaryAssessment: {
      factors: [
        { factor: 'Trail Growth', status: '+17% (accelerating to +22%)', impact: 'Positive' },
        { factor: 'Broker Growth', status: 'Multiple 100%+ growers', impact: 'Positive' },
        { factor: 'Broker Retention', status: 'Zero attrition', impact: 'Positive' },
        { factor: 'Clawback Rate', status: '~1%', impact: 'Positive' },
        { factor: 'Non-Mortgage Income', status: '$102k additional', impact: 'Positive' },
        { factor: 'FY26 Forecast', status: '$1.27M (+14.8%)', impact: 'Positive' },
      ],
      valuationRange: {
        conservative: { multiple: 4, value: 4417801 },
        midRange: { multiple: 5, value: 5522251 },
        premium: { multiple: 6, value: 6626701 },
        forecastBased: { multiple: 6, value: 7608888 },
      },
      verdict: 'CAUTION',
    },
    createdAt: new Date().toISOString(),
  };
}
